import { Combobox } from "@kobalte/core/combobox"
import { type AccentHue, colorize } from "@repo/fika-design-system/color"
import type { TagTargetType } from "@repo/fika-shared/src/loader/tags-schema"
import { createQuery } from "@urql/solid"
import { createEffect, createMemo, createSignal, For, Show } from "solid-js"
import { graphql } from "@/graphql"
import {
  createMutationWithErrorHandling,
  ErrorFragment,
} from "@/lib/graphql-mutation-helpers"
import { Box } from "../components/box"
import { Icon } from "../components/icon"
import { pillRecipe } from "./pills.css"
import {
  comboboxContentClass,
  comboboxControlClass,
  comboboxFieldClass,
  comboboxInputClass,
  comboboxItemClass,
  comboboxItemIndicatorClass,
  comboboxLabelClass,
  comboboxListboxClass,
  comboboxRemoveButtonClass,
  comboboxTagsClass,
  tagSuggestionClass,
  tagSuggestionListClass,
} from "./tags-with-suggestions.css"

export function TagWithSuggestions(props: {
  tags: Set<string>
  suggestions: string | null
  targetType: TagTargetType
  targetId: string
  hue: AccentHue
}) {
  const SetTagsMutation = graphql(
    `mutation SetTags($targetId: ID!, $targetType: TagTargetType!, $tags: [String!]!) {
      setTags(targetId: $targetId, targetType: $targetType, tags: $tags) {
        ...on MutationSetTagsSuccess {
          data {
            id
            tags
          }
        }
        ...ErrorFragment
      }
    }`,
    [ErrorFragment],
  )
  const [, setTags] = createMutationWithErrorHandling(
    SetTagsMutation,
    "setTags",
  )
  const TagOptionsQuery = graphql(
    `query TagOptions($targetType: TagTargetType!) {
      tagOptions(targetType: $targetType)
    }`,
  )
  const [tagOptionsResult] = createQuery({
    query: TagOptionsQuery,
    variables: () => ({ targetType: props.targetType }),
  })

  const tagOptions = createMemo(() => tagOptionsResult.data?.tagOptions ?? [])
  const [inputValue, setInputValue] = createSignal<string | undefined>()
  const [inputRef, setInputRef] = createSignal<HTMLInputElement>()
  const [isOpen, setIsOpen] = createSignal(false)

  const options = createMemo(() => {
    const val = inputValue()
    const set = new Set(
      tagOptions()
        .concat(Array.from(props.tags))
        .concat(val ? [val] : []),
    )
    return Array.from(set)
  })

  createEffect(() => {
    const input = inputRef()
    const open = isOpen()
    const opts = options()
    const value = Array.from(props.tags)

    if (input && open && opts.length > 0 && value) {
      setTimeout(() => {
        const homeEvent = new KeyboardEvent("keydown", {
          key: "Home",
          code: "Home",
          keyCode: 36,
          which: 36,
          bubbles: true,
          cancelable: true,
        })
        input.dispatchEvent(homeEvent)
      }, 10)
    }
  })

  async function onChange(tags: string[]) {
    await setTags({
      targetId: props.targetId,
      targetType: props.targetType,
      tags,
    })
  }

  function onClickSuggestion(tag: string) {
    if (props.tags.has(tag)) return
    onChange([...Array.from(props.tags), tag])
  }

  return (
    <Box gap="12">
      <Combobox<string>
        multiple
        triggerMode="input"
        options={options()}
        value={Array.from(props.tags)}
        onChange={onChange}
        placeholder="Add a tag"
        class={comboboxFieldClass}
        onInputChange={value => setInputValue(value)}
        onOpenChange={setIsOpen}
        removeOnBackspace
        itemComponent={iProps => (
          <Show when={!Array.from(props.tags).includes(iProps.item.rawValue)}>
            <Combobox.Item
              item={iProps.item}
              class={comboboxItemClass({ hue: props.hue })}
            >
              <Combobox.ItemLabel>{iProps.item.rawValue}</Combobox.ItemLabel>
              <Combobox.ItemIndicator class={comboboxItemIndicatorClass}>
                <Icon name="check" />
              </Combobox.ItemIndicator>
            </Combobox.Item>
          </Show>
        )}
      >
        <Combobox.Label class={comboboxLabelClass}>
          <Icon name="tag" size="18" color={colorize(props.hue, "600")} />
          tags:
        </Combobox.Label>
        <Combobox.Control<string> class={comboboxControlClass}>
          {state => (
            <div class={comboboxTagsClass}>
              <For each={state.selectedOptions()}>
                {option => (
                  <span
                    onPointerDown={e => e.stopPropagation()}
                    class={pillRecipe({ hue: "sand", rounded: true })}
                  >
                    {option}
                    <button
                      type="button"
                      class={comboboxRemoveButtonClass({ hue: "sand" })}
                      onClick={() => state.remove(option)}
                    >
                      <Icon name="x" size="18" />
                    </button>
                  </span>
                )}
              </For>
              <Combobox.Input
                ref={setInputRef}
                class={comboboxInputClass}
                placeholder="Add a tag"
              />
            </div>
          )}
        </Combobox.Control>
        <Combobox.Portal>
          <Combobox.Content class={comboboxContentClass}>
            <Show
              when={!tagOptionsResult.fetching}
              fallback={
                <div class={comboboxListboxClass}>
                  <Box padding="12" alignItems="center" justifyContent="center">
                    <Icon
                      name="loader"
                      size="24"
                      color="sand500"
                      class="animate-spin"
                    />
                  </Box>
                </div>
              }
            >
              <Combobox.Listbox class={comboboxListboxClass} />
            </Show>
          </Combobox.Content>
        </Combobox.Portal>
      </Combobox>
      <TagSuggestions
        suggestions={props.suggestions}
        tags={props.tags}
        onClickSuggestion={onClickSuggestion}
      />
    </Box>
  )
}

function TagSuggestions(props: {
  suggestions: string | null
  tags?: Set<string>
  onClickSuggestion?: (tag: string) => void
}) {
  return (
    <Show when={props.suggestions}>
      {suggestions => (
        <ul class={tagSuggestionListClass}>
          <For
            each={suggestions()
              .split(",")
              .filter(tag => !props.tags?.has(tag))}
          >
            {tag => (
              <li>
                <button
                  type="button"
                  class={tagSuggestionClass}
                  onClick={() => props.onClickSuggestion?.(tag)}
                >
                  {tag}
                </button>
              </li>
            )}
          </For>
        </ul>
      )}
    </Show>
  )
}
